import React, { useEffect, useState, useCallback, useRef } from "react";
import { X, MessageSquare, Send, CheckCircle, Loader2, Minus, GripHorizontal } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

/**
 * QuickQuoteWidget — Draggable, minimizable floating quick quote button + form.
 */
function QuickQuoteWidget({ quickQuote, accentColor, showQuickQuote, setShowQuickQuote, qqForm, setQqForm, qqSending, qqDone, handleQQSubmit, setQqDone }) {
  const [minimized, setMinimized] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef(null);
  const startRef = useRef({ x: 0, y: 0, posX: 0, posY: 0 });

  const onPointerDown = (e) => {
    if (showQuickQuote) return; // Don't drag when form is open
    setDragging(true);
    startRef.current = { x: e.clientX, y: e.clientY, posX: pos.x, posY: pos.y };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e) => {
    if (!dragging) return;
    const dx = e.clientX - startRef.current.x;
    const dy = e.clientY - startRef.current.y;
    setPos({ x: startRef.current.posX + dx, y: startRef.current.posY + dy });
  };

  const onPointerUp = (e) => {
    if (!dragging) return;
    setDragging(false);
    // If barely moved, treat as click
    const dx = Math.abs(e.clientX - startRef.current.x);
    const dy = Math.abs(e.clientY - startRef.current.y);
    if (dx < 5 && dy < 5 && !minimized) {
      setShowQuickQuote(true);
    }
  };

  // Minimized state — just a small icon
  if (minimized && !showQuickQuote) {
    return (
      <button
        onClick={() => setMinimized(false)}
        className="fixed bottom-5 right-5 z-[55] h-11 w-11 rounded-full shadow-lg flex items-center justify-center text-white transition-all hover:scale-110"
        style={{ backgroundColor: accentColor, transform: `translate(${pos.x}px, ${pos.y}px)` }}
        data-testid="quick-quote-minimized"
      >
        <MessageSquare className="h-5 w-5" />
      </button>
    );
  }

  return (
    <>
      {/* Floating button — draggable */}
      {!showQuickQuote && (
        <div
          ref={dragRef}
          className="fixed bottom-5 right-5 z-[55] flex items-center gap-1 select-none"
          style={{ transform: `translate(${pos.x}px, ${pos.y}px)`, touchAction: "none" }}
          data-testid="quick-quote-fab"
        >
          <button
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            className="shadow-xl hover:shadow-2xl text-white rounded-full pl-4 pr-5 py-3.5 flex items-center gap-2 text-sm font-bold transition-all"
            style={{ backgroundColor: accentColor, cursor: dragging ? "grabbing" : "grab" }}
          >
            <MessageSquare className="h-4 w-4" />
            <span className="hidden sm:inline">{quickQuote.config?.button_text || "Get a Quick Quote"}</span>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setMinimized(true); }}
            className="h-8 w-8 rounded-full bg-white shadow-md flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors"
            title="Minimize"
            data-testid="quick-quote-minimize"
          >
            <Minus className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Quote form */}
      {showQuickQuote && !qqDone && (
        <div className="fixed bottom-5 right-5 z-[65] w-[340px] max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden animate-in slide-in-from-bottom-4 duration-300"
          style={{ transform: `translate(${pos.x}px, ${pos.y}px)` }}
          data-testid="quick-quote-form">
          <div className="p-4 text-white" style={{ backgroundColor: accentColor }}>
            <div className="flex items-center justify-between">
              <p className="font-bold text-sm">Quick Quote Request</p>
              <button onClick={() => setShowQuickQuote(false)} className="p-1 rounded hover:bg-white/20"><X className="h-3.5 w-3.5" /></button>
            </div>
            <p className="text-xs opacity-80 mt-0.5">We'll get back to you within 1 hour</p>
          </div>
          <div className="p-4 space-y-3">
            <input className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm" placeholder="Your name *"
              value={qqForm.name} onChange={e => setQqForm(f => ({ ...f, name: e.target.value }))} data-testid="qq-name" />
            <input className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm" placeholder="Email *" type="email"
              value={qqForm.email} onChange={e => setQqForm(f => ({ ...f, email: e.target.value }))} data-testid="qq-email" />
            <input className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm" placeholder="Phone"
              value={qqForm.phone} onChange={e => setQqForm(f => ({ ...f, phone: e.target.value }))} data-testid="qq-phone" />
            <input className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm" type="date"
              value={qqForm.event_date} onChange={e => setQqForm(f => ({ ...f, event_date: e.target.value }))} data-testid="qq-date" />
            <select className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-600"
              value={qqForm.event_type} onChange={e => setQqForm(f => ({ ...f, event_type: e.target.value }))} data-testid="qq-event-type">
              <option value="">Event type...</option>
              <option value="birthday">Birthday Party</option>
              <option value="wedding">Wedding</option>
              <option value="corporate">Corporate Event</option>
              <option value="school">School/Church</option>
              <option value="other">Other</option>
            </select>
            <button onClick={handleQQSubmit} disabled={qqSending || !qqForm.name || !qqForm.email}
              className="w-full py-3 rounded-xl text-white font-bold text-sm disabled:opacity-50 flex items-center justify-center gap-2 transition-all hover:opacity-90"
              style={{ backgroundColor: accentColor }} data-testid="qq-submit">
              {qqSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4" /> Send Quote Request</>}
            </button>
          </div>
        </div>
      )}

      {/* Success state */}
      {showQuickQuote && qqDone && (
        <div className="fixed bottom-5 right-5 z-[65] w-[340px] max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-2xl border border-slate-200 p-6 text-center animate-in fade-in duration-300"
          style={{ transform: `translate(${pos.x}px, ${pos.y}px)` }}
          data-testid="quick-quote-success">
          <CheckCircle className="h-10 w-10 mx-auto mb-2" style={{ color: accentColor }} />
          <p className="font-bold text-slate-900 mb-1">Quote Request Sent!</p>
          <p className="text-xs text-slate-500 mb-3">We'll get back to you shortly.</p>
          <button onClick={() => { setShowQuickQuote(false); setQqDone(false); setQqForm({ name: "", email: "", phone: "", event_date: "", event_type: "" }); }}
            className="text-sm font-semibold" style={{ color: accentColor }}>Close</button>
        </div>
      )}
    </>
  );
}

/**
 * SiteWidgetRenderer — Injects marketing widgets (FOMO, Exit-Intent, Quick Quote, Timed Offer)
 * into the public-facing tenant site. SEO-safe: no layout shift, delayed rendering,
 * mobile-respectful (exit-intent disabled on mobile by default per Google guidelines).
 */
export default function SiteWidgetRenderer({ micrositeId, accentColor = "#0f766e" }) {
  const [widgets, setWidgets] = useState([]);
  const [fomoFeed, setFomoFeed] = useState([]);
  const [fomoIdx, setFomoIdx] = useState(0);
  const [showFomo, setShowFomo] = useState(false);
  const [showExitIntent, setShowExitIntent] = useState(false);
  const [showTimedOffer, setShowTimedOffer] = useState(false);
  const [showQuickQuote, setShowQuickQuote] = useState(false);
  const [qqForm, setQqForm] = useState({ name: "", email: "", phone: "", event_date: "", event_type: "" });
  const [qqSending, setQqSending] = useState(false);
  const [qqDone, setQqDone] = useState(false);
  const [exitForm, setExitForm] = useState({ email: "" });
  const [exitSending, setExitSending] = useState(false);
  const [exitDone, setExitDone] = useState(false);

  const exitShownRef = useRef(false);
  const fomoTimerRef = useRef(null);
  const fomoCountRef = useRef(0);

  // Load widget configs
  useEffect(() => {
    if (!micrositeId) return;
    fetch(`${API}/api/public/site-widgets/${micrositeId}`)
      .then(r => r.json())
      .then(d => setWidgets(d || []))
      .catch(() => {});
  }, [micrositeId]);

  const getWidget = useCallback((type) => widgets.find(w => w.widget_type === type), [widgets]);

  // ── FOMO Popup ──
  useEffect(() => {
    const fomo = getWidget("fomo_popup");
    if (!fomo || !micrositeId) return;
    fetch(`${API}/api/public/fomo-feed/${micrositeId}`)
      .then(r => r.json())
      .then(d => { if (d && d.length) setFomoFeed(d); })
      .catch(() => {});
  }, [getWidget, micrositeId]);

  useEffect(() => {
    const fomo = getWidget("fomo_popup");
    if (!fomo || fomoFeed.length === 0) return;
    const cfg = fomo.config || {};
    const delay = (cfg.delay_seconds || 8) * 1000;
    const interval = (cfg.interval_seconds || 45) * 1000;
    const maxShows = cfg.max_shows || 5;

    const showNext = () => {
      if (fomoCountRef.current >= maxShows) return;
      setFomoIdx(prev => prev % fomoFeed.length);
      setShowFomo(true);
      fomoCountRef.current += 1;
      setTimeout(() => setShowFomo(false), 5000);
      setFomoIdx(prev => (prev + 1) % fomoFeed.length);
    };

    const initialTimer = setTimeout(() => {
      showNext();
      fomoTimerRef.current = setInterval(showNext, interval);
    }, delay);

    return () => {
      clearTimeout(initialTimer);
      if (fomoTimerRef.current) clearInterval(fomoTimerRef.current);
    };
  }, [getWidget, fomoFeed]);

  // ── Exit Intent ──
  useEffect(() => {
    const exit = getWidget("exit_intent");
    if (!exit) return;
    const cfg = exit.config || {};
    // Use localStorage with 7-day TTL instead of sessionStorage
    const dismissKey = `prs_exit_dismissed_${micrositeId || "global"}`;
    const dismissed = localStorage.getItem(dismissKey);
    if (dismissed) {
      const dismissedAt = parseInt(dismissed, 10);
      const sevenDays = 7 * 24 * 60 * 60 * 1000;
      if (Date.now() - dismissedAt < sevenDays) return;
    }
    const isMobile = window.innerWidth < 768;
    if (isMobile && !cfg.mobile_enabled) return;

    const handler = (e) => {
      if (e.clientY < 10 && !exitShownRef.current) {
        exitShownRef.current = true;
        setShowExitIntent(true);
        localStorage.setItem(dismissKey, String(Date.now()));
      }
    };
    document.addEventListener("mouseleave", handler);
    return () => document.removeEventListener("mouseleave", handler);
  }, [getWidget, micrositeId]);

  // ── Timed Offer ──
  useEffect(() => {
    const timed = getWidget("timed_offer");
    if (!timed) return;
    if (sessionStorage.getItem("prs_timed_dismissed")) return;
    const delay = (timed.config?.delay_seconds || 20) * 1000;
    const timer = setTimeout(() => setShowTimedOffer(true), delay);
    return () => clearTimeout(timer);
  }, [getWidget]);

  // ── Lead Capture Submit ──
  const submitLead = async (formData, widgetType) => {
    await fetch(`${API}/api/public/lead-capture`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...formData, microsite_id: micrositeId, widget_type: widgetType }),
    });
  };

  const handleExitSubmit = async () => {
    if (!exitForm.email) return;
    setExitSending(true);
    try {
      await submitLead({ email: exitForm.email, source: "exit_intent" }, "exit_intent");
      setExitDone(true);
    } catch {}
    setExitSending(false);
  };

  const handleQQSubmit = async () => {
    if (!qqForm.email || !qqForm.name) return;
    setQqSending(true);
    try {
      await submitLead({ ...qqForm, source: "quick_quote" }, "quick_quote");
      setQqDone(true);
    } catch {}
    setQqSending(false);
  };

  const quickQuote = getWidget("quick_quote");

  return (
    <>
      {/* ── FOMO Social Proof Toast ── */}
      <div className={`fixed bottom-20 right-4 z-[55] transition-all duration-500 ${showFomo && !showQuickQuote ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0 pointer-events-none"}`}
        data-testid="fomo-popup" role="status" aria-live="polite">
        {fomoFeed[fomoIdx % Math.max(fomoFeed.length, 1)] && (() => {
          const entry = fomoFeed[fomoIdx % fomoFeed.length];
          const fomoWidget = getWidget("fomo_popup");
          const fomoTitle = fomoWidget?.config?.title || "Recent Activity";
          const displayName = (entry?.name && entry.name.toLowerCase() !== "test") ? entry.name : "A customer";
          return (
            <div className="bg-white rounded-xl shadow-2xl border border-slate-200 p-3.5 max-w-[320px]">
              <p className="text-[9px] font-bold uppercase tracking-wider mb-1.5" style={{ color: accentColor }}>{fomoTitle}</p>
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                  style={{ backgroundColor: accentColor }}>
                  {displayName[0].toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-bold text-slate-800 truncate">
                    {displayName}{entry?.city ? ` from ${entry.city}` : ""}
                  </p>
                  <p className="text-[11px] text-slate-500 truncate">
                    just booked <span className="font-semibold" style={{ color: accentColor }}>{entry?.item || "a rental"}</span>
                  </p>
                  <p className="text-[10px] text-slate-400">{entry?.time_ago}</p>
                </div>
              </div>
            </div>
          );
        })()}
      </div>

      {/* ── Exit-Intent Popup ── */}
      {showExitIntent && !exitDone && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="exit-intent-popup">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-8 relative animate-in fade-in zoom-in-95 duration-300">
            <button onClick={() => setShowExitIntent(false)} className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-slate-100 text-slate-400">
              <X className="h-4 w-4" />
            </button>
            <div className="text-center">
              <div className="h-14 w-14 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: `${accentColor}15` }}>
                <MessageSquare className="h-7 w-7" style={{ color: accentColor }} />
              </div>
              <h3 className="text-xl font-black text-slate-900 mb-1">
                {getWidget("exit_intent")?.config?.headline || "Wait! Don't miss out!"}
              </h3>
              <p className="text-sm text-slate-500 mb-6">
                {getWidget("exit_intent")?.config?.subtext || "Get 10% off your first booking"}
              </p>
              <div className="flex gap-2">
                <input
                  type="email" placeholder="Enter your email"
                  className="flex-1 px-4 py-3 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:border-transparent"
                  style={{ "--tw-ring-color": accentColor }}
                  value={exitForm.email} onChange={e => setExitForm({ email: e.target.value })}
                  data-testid="exit-intent-email"
                />
                <button onClick={handleExitSubmit} disabled={exitSending || !exitForm.email}
                  className="px-5 py-3 rounded-xl text-white font-bold text-sm disabled:opacity-50 transition-all hover:opacity-90"
                  style={{ backgroundColor: accentColor }} data-testid="exit-intent-submit">
                  {exitSending ? <Loader2 className="h-4 w-4 animate-spin" /> : (getWidget("exit_intent")?.config?.cta_text || "Claim My Discount")}
                </button>
              </div>
              <p className="text-[10px] text-slate-400 mt-3">No spam. Unsubscribe anytime.</p>
            </div>
          </div>
        </div>
      )}
      {showExitIntent && exitDone && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="exit-intent-success">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full mx-4 p-8 text-center">
            <CheckCircle className="h-12 w-12 mx-auto mb-3" style={{ color: accentColor }} />
            <h3 className="text-lg font-bold text-slate-900 mb-1">You're in!</h3>
            <p className="text-sm text-slate-500 mb-4">Check your email for your exclusive discount code.</p>
            <button onClick={() => setShowExitIntent(false)} className="text-sm font-semibold" style={{ color: accentColor }}>Continue browsing</button>
          </div>
        </div>
      )}

      {/* ── Timed Offer Banner ── */}
      {showTimedOffer && (
        <div className="fixed top-0 left-0 right-0 z-[55] animate-in slide-in-from-top duration-500" data-testid="timed-offer-banner">
          <div className="text-white py-2.5 px-4 flex items-center justify-center gap-3 text-sm" style={{ backgroundColor: accentColor }}>
            <span className="font-bold">{getWidget("timed_offer")?.config?.headline || "Limited Time Offer!"}</span>
            <span className="opacity-80">{getWidget("timed_offer")?.config?.body || ""}</span>
            {getWidget("timed_offer")?.config?.cta_text && (
              <a href={getWidget("timed_offer")?.config?.cta_url || "/catalog"}
                className="ml-2 px-3 py-1 rounded-full bg-white/20 hover:bg-white/30 text-xs font-bold transition-colors">
                {getWidget("timed_offer").config.cta_text}
              </a>
            )}
            <button onClick={() => { setShowTimedOffer(false); sessionStorage.setItem("prs_timed_dismissed", "1"); }}
              className="ml-2 p-0.5 rounded hover:bg-white/20 transition-colors">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* ── Quick Quote Floating Button ── */}
      {quickQuote && (
        <QuickQuoteWidget
          quickQuote={quickQuote}
          accentColor={accentColor}
          showQuickQuote={showQuickQuote}
          setShowQuickQuote={setShowQuickQuote}
          qqForm={qqForm}
          setQqForm={setQqForm}
          qqSending={qqSending}
          qqDone={qqDone}
          handleQQSubmit={handleQQSubmit}
          setQqDone={setQqDone}
        />
      )}
    </>
  );
}
